/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import {
	emptyStrapiContext,
	type StrapiContext as IStrapiContext,
} from '@/interfaces/context/strapi.context.interface';
import type { Category, GlobalData, Menu, StrapiListRequest, StrapiListResponse } from '@/interfaces/strapi';
import { getBlogCategories, getGlobalStrapiData, getMenuStrapi } from '@/services/strapi';
import { Loading } from '@/components/Loading';
import { useStrapiApi } from '@/hooks/useStrapiApi';

type StrapiProviderProps = {
	readonly children: ReactNode;
};

const contextDefaultValue: IStrapiContext = emptyStrapiContext;
const StrapiContext = createContext(contextDefaultValue);

const StrapiProvider = ({ children }: StrapiProviderProps) => {
	const {
		loading: globalLoading,
		data: globalData,
		error: globalError,
	} = useStrapiApi<GlobalData, null>(getGlobalStrapiData, { autoFetch: true, params: null });
	const {
		loading: menuLoading,
		data: menuData,
		error: menuError,
	} = useStrapiApi<Menu, null>(getMenuStrapi, { autoFetch: true, params: null });

	const categoryParams = useMemo<StrapiListRequest>(
		() => ({
			populate: { image: true },
		}),
		[],
	);

	const {
		loading: categoriesLoading,
		data: categoriesData,
		error: categoriesError,
	} = useStrapiApi<StrapiListResponse<Category>, StrapiListRequest>(getBlogCategories, {
		autoFetch: true,
		params: categoryParams,
	});

	const isLoading = (globalLoading || menuLoading || categoriesLoading) ?? false;
	const isError = globalError || menuError || categoriesError;

	useEffect(() => {
		if (isError) console.error('Error getting data: ', isError.message);
	}, [isError]);

	const value = useMemo(() => {
		if (!globalData || !menuData || !categoriesData) return emptyStrapiContext;

		const contextData: IStrapiContext = {
			...globalData,
			menu: { ...menuData },
			blogCategories: categoriesData.list,
		};
		return contextData;
	}, [globalData, menuData, categoriesData]);

	if (isLoading) return <Loading isFull />;
	return <StrapiContext.Provider value={value}>{children}</StrapiContext.Provider>;
};

const useStrapiGlobal = () => {
	const context = useContext(StrapiContext);

	if (context === undefined) {
		throw new Error('useStrapiGlobal must be used within provider');
	}

	return context;
};

export { StrapiContext, StrapiProvider, useStrapiGlobal };
